import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useOffline } from '../context/OfflineContext';
import { statsAPI } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Mail,
  Calendar,
  Users,
  CheckSquare,
  Settings,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  ChevronLeft,
  Bell,
  Wifi,
  WifiOff,
  CloudOff,
  Loader2,
  Send,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const navItems = [
  { icon: Mail, label: 'Email', path: '/email', badge: 'unread_emails' },
  { icon: Calendar, label: 'Calendrier', path: '/calendar', badge: 'today_events' },
  { icon: Users, label: 'Contacts', path: '/contacts', badge: 'total_contacts' },
  { icon: CheckSquare, label: 'Tâches', path: '/tasks', badge: 'pending_tasks' },
];

export default function DashboardLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isOnline, isSyncing, outboxCount } = useOffline();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appConfig, setAppConfig] = useState({ app_name: 'ProMail', primary_color: '#0066DA', logo_url: '' });
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);

  const handleWsMessage = useCallback((data) => {
    // Bug fix: backend sends type 'new_emails' (plural), not 'new_email'
    if (data.type === 'new_emails' || data.type === 'new_email') {
      toast.info(`${data.count} nouveau(x) email(s) reçu(s)`, {
        action: {
          label: 'Voir',
          onClick: () => navigate('/email/INBOX'),
        },
      });
      loadStats();
      setNotifications(prev => [...prev, {
        id: Date.now(),
        text: `${data.count} nouveau(x) email(s)`,
        time: new Date().toISOString(),
        read: false,
      }]);
    }
  }, [navigate]);

  const { connected } = useWebSocket(handleWsMessage);

  useEffect(() => {
    loadAppConfig();
    loadStats();
    const interval = setInterval(loadStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadAppConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/config/public`);
      setAppConfig(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await statsAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    // Bug fix #7: use spread operator to correctly iterate Unicode code points
    // This handles multi-byte characters (CJK, Cyrillic, Arabic, emoji…)
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return ([...words[0]][0] + [...words[1]][0]).toUpperCase().slice(0, 2);
    }
    // Single word or single-part name: take first two code points
    const codePoints = [...name.trim()];
    return codePoints.slice(0, 2).join('').toUpperCase();
  };

  const isActive = (path) => {
    if (path === '/email') {
      return location.pathname === '/email' || location.pathname.startsWith('/email/');
    }
    return location.pathname === path;
  };

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out",
          sidebarOpen ? "w-64" : "w-16",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-3 overflow-hidden">
            {appConfig.logo_url ? (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-white">
                <img src={appConfig.logo_url} alt="Logo" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div 
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: appConfig.primary_color || '#0066DA' }}
              >
                <Mail className="w-5 h-5 text-white" />
              </div>
            )}
            {sidebarOpen && (
              <span className="font-bold text-lg font-['Plus_Jakarta_Sans'] truncate">
                {appConfig.app_name || 'ProMail'}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={cn("w-4 h-4 transition-transform", !sidebarOpen && "rotate-180")} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {navItems.map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-3 h-11 sidebar-item",
                  isActive(item.path) && "bg-primary/10 text-primary border-l-2 border-primary rounded-l-none",
                  !sidebarOpen && "justify-center px-0"
                )}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {stats[item.badge] > 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary text-primary-foreground">
                        {stats[item.badge]}
                      </span>
                    )}
                  </>
                )}
              </Button>
            ))}
          </nav>

          <Separator className="my-4 mx-3" />

          <div className="px-3">
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start gap-3 h-11 sidebar-item",
                isActive('/settings') && "bg-primary/10 text-primary",
                !sidebarOpen && "justify-center px-0"
              )}
              onClick={() => {
                navigate('/settings');
                setMobileMenuOpen(false);
              }}
              data-testid="nav-settings"
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Paramètres</span>}
            </Button>
          </div>
        </ScrollArea>

        {/* User section */}
        <div className="p-3 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-3 h-12",
                  !sidebarOpen && "justify-center px-0"
                )}
                data-testid="user-menu-trigger"
              >
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                {sidebarOpen && (
                  <div className="flex-1 text-left overflow-hidden">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle-menu">
                {theme === 'dark' ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-16 flex items-center justify-between px-4 lg:px-6 border-b border-border bg-card">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] capitalize">
              {location.pathname.split('/')[1] || 'Email'}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 mr-2" data-testid="ws-status">
              {connected ? (
                <Wifi className="w-4 h-4 text-green-500" />
              ) : (
                <WifiOff className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {(stats.unread_emails > 0 || notifications.filter(n => !n.read).length > 0) && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-destructive" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Aucune notification
                  </div>
                ) : (
                  notifications.slice(-5).reverse().map((notif) => (
                    <DropdownMenuItem key={notif.id} className="flex flex-col items-start gap-1 p-3">
                      <span className="text-sm">{notif.text}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(notif.time).toLocaleTimeString('fr-FR')}
                      </span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-header">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </header>

        {/* Offline/Syncing banner */}
        {(!isOnline || isSyncing || outboxCount > 0) && (
          <div
            className={cn(
              "flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all",
              !isOnline && "bg-amber-500/15 text-amber-700 dark:text-amber-400",
              isOnline && isSyncing && "bg-blue-500/15 text-blue-700 dark:text-blue-400",
              isOnline && !isSyncing && outboxCount > 0 && "bg-orange-500/15 text-orange-700 dark:text-orange-400",
            )}
            data-testid="offline-banner"
          >
            {!isOnline && (
              <>
                <CloudOff className="w-4 h-4" />
                <span>Mode hors-ligne — Vos données en cache sont disponibles</span>
                {outboxCount > 0 && (
                  <span className="ml-auto flex items-center gap-1.5 bg-amber-500/20 px-2.5 py-0.5 rounded-full text-xs">
                    <Send className="w-3 h-3" />
                    {outboxCount} email(s) en attente
                  </span>
                )}
              </>
            )}
            {isOnline && isSyncing && (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Synchronisation en cours...</span>
              </>
            )}
            {isOnline && !isSyncing && outboxCount > 0 && (
              <>
                <Send className="w-4 h-4" />
                <span>{outboxCount} email(s) en attente d'envoi</span>
              </>
            )}
          </div>
        )}

        {/* Page content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
